/**
 * CX / Classnames
 *
 * A simple utility to concatenate class names
 * @example
 * cx("a", "b", "c")               === "a b c" // Basic usage
 * cx("a b", "b", "b", "  c b  ")  === "a b c" // Duplicate values + whitespace
 * cx(["a", "b"], "c")             === "a b c" // Nested arrays
 * cx(false, "b", null, "c", 0)    === "b c"   // Falsy values
 * cx({ a: 'truthy', b: false })   === "a"     // Object values
 *
 * @see https://github.com/JedWatson/classnames - Original implementation
 * @param {...(string | string[] | Record<string, any> | undefined)} classes - Classnames
 * @returns {string} Concatenated class names
 */
export const cx = (...classes) =>
  // Let's break this down:
  //   [...new Set(...)]     - Remove duplicate class declarations
  //   classes.flatMap(...)  - Unwraps nested arrays: [["a", "b"], "c"] => ["a", "b", "c"]
  //   classify.any          - Convert all values to strings or arrays of strings
  //   .filter(Boolean)      - Remove all falsy values
  //   .join(" ")            - Join everything back into a string
  [...new Set(classes.flatMap(classify.any))].filter(Boolean).join(" ");

const classify = {
  /** @type {(input: any) => string|string[]|false} */
  any(input) {
    if (!input) return false;
    // Run the appropriate classification method based on the input type, or return false
    return classify[typeof input]?.(input) || false;
  },
  /** @type {(str: string) => string|string[]} */
  string(str) {
    str = str.trim();
    // Handle multiple classes in a single string
    if (WHITESPACE.test(str)) return str.split(WHITESPACE);
    return str;
  },
  object(obj) {
    // Handle null
    if (!obj) return false;
    // Handle arrays by recursively calling this function
    if (Array.isArray(obj)) return obj.flatMap(classify.any);
    // Handle objects by returning the keys if the value is truthy
    return Object.entries(obj).flatMap(([key, value]) => !!value && classify.string(key));
  },
};

const WHITESPACE = /\s+/;

// map of digits to month strings
export const monthsMap = {
  0: "January",
  1: "February",
  2: "March",
  3: "April",
  4: "May",
  5: "June",
  6: "July",
  7: "August",
  8: "September",
  9: "October",
  10: "November",
  11: "December",
};

/**
 * Format date to Eastern Standard Time
 */
export const formatDateToEst = (date = Date.now(), day = true) => {
  const normalizedDate = normalizeDate(date);
  const formatter = day ? estDateFormatter : estDateFormatterWithoutDay;
  return estDateFormatter.format(normalizedDate);
};

/**
 * Fix spec error where dates without time are interpreted in UTC rather than local time
 * @see https://maggiepint.com/2017/04/11/fixing-javascript-date-web-compatibility-and-reality/
 * @param {string|number|Date} date
 * @returns {Date}
 */
const normalizeDate = (date) => {
  // When the time zone offset is absent, date-only forms are interpreted as a
  // UTC time and date-time forms are interpreted as a local time.
  if (typeof date === "string" && !date.includes("T")) {
    return new Date(date + "T00:00:00");
  }

  return new Date(date);
};

const estDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "long",
  day: "numeric",
});
const estDateFormatterWithoutDay = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "long",
});

/**
 * @function formatDate
 * @param {String | Date.now()} release_date
 * @param {Boolean} day defaults to true, returning "July 23, 2023." If set to false omits the day, returning "July 2023"
 * @returns {String} formatted date
 */
export const formatDate = (release_date = Date.now(), day = true) => {
  const releaseDate = new Date(release_date);
  const monthStr = monthsMap[releaseDate.getUTCMonth()];
  const date = releaseDate.getUTCDate();
  const year = releaseDate.getUTCFullYear();

  if (!day) {
    return `${monthStr} ${year}`;
  }

  return `${monthStr} ${date}, ${year}`;
};

/**
 * @function formatAuthors
 * @param {Array} authors
 * @returns {String} formatted string for author/authors
 */
export const formatAuthors = (authors = []) => {
  let authorsName;

  // only one author
  if (authors.length === 1) {
    authorsName = authors[0].name;
  } else {
    // multiple authors
    const allAuthors = authors.map((author) => author["name"]);
    authorsName = allAuthors.join(", ");
  }

  return authorsName;
};

/**
 * Oxford comma list formatting
 *
 * Formats a list, complements any kind of array (think JSX elements)
 *
 * @usage
 * ```js
 * const list = ["a", "b", "c"];
 * const formatted = list.map((str, i) => str + oxford(i, list.length, 'or'));
 * formatted.join('') === "a, b, or c"
 * ```
 */
export const oxford = (i, length, conjugation = "and") =>
  i + 1 === length
    ? "" // end of list
    : length === 2
      ? ` ${conjugation} ` // two items (" and ")
      : ", " + // list of three or more items, not the last item
        (i + 2 === length
          ? // penultimate item (", and ")
            conjugation + " "
          : // middle item (", ")
            "");

/**
 * @typedef {{
 *   slug: string,
 *   title: string,
 *   short: string,
 *   type: string
 * }}
 */

/**
 * This is used to map publication keys to their corresponding names
 *
 * Keys are slugs, values are the publication term
 * @type {Record<string, Record<string, string>>}
 */
export const PUBLICATION_TERMS = {
  sa: {
    type: "magazine",
    slug: "sa",
    title: "Scientific American Magazine",
    short: "Scientific American",
  },
  builders: { type: "magazine", slug: "builders", title: "SA Builders", short: "Builders" },
  supplements: {
    type: "magazine",
    slug: "supplements",
    title: "SA Supplements",
    short: "Supplements",
  },
  mind: { type: "magazine", slug: "mind", title: "SA Mind", short: "Mind" },
  "special-editions": {
    type: "magazine",
    slug: "special-editions",
    title: "SA Special Editions",
    short: "Special Edition",
  },
  "health-medicine": {
    type: "magazine",
    slug: "health-medicine",
    title: "SA Health & Medicine",
    short: "Health & Medicine",
  },
  "space-physics": {
    type: "magazine",
    slug: "space-physics",
    title: "SA Space & Physics",
    short: "Space & Physics",
  },
};
const PUBLICATION_TERMS_ARRAY = Object.values(PUBLICATION_TERMS);
/**
 * Find a publication term by key
 * @param {string} term - Publication term to find
 * @param {keyof PublicationTerm} [byKey="title"] - Key to search by, defaults to `"title"`
 */
export const findPublicationTerm = (term, byKey = "title") =>
  PUBLICATION_TERMS_ARRAY.find((pub) => pub[byKey] === term);

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  timeZone: "UTC",
});

/**
 * Get url for an issue. Receives an issue object
 * but only needs two keys.
 *
 * @param {{
 *  magazine_title: string,
 *  path: string,
 * }} issue
 */
export function getLinkForIssue({ magazine_title, path }) {
  // New CMS gives us usable links
  if (path.startsWith("/issue/")) {
    return path;
  }

  // Mura gives us fragment to assemble
  const publication = findPublicationTerm(magazine_title);
  const pubKey = publication?.slug || "sa";
  return `/issue/${pubKey}/${path}/`;
}

/**
 * Handle double issues
 * Don't worry about Mind etc., they were all discontinued
 * before this.
 * @param {String} dateStr - issue_date string
 */
export const formatIssueDisplayDate = (dateStr) => {
  const date = new Date(dateStr);
  let displayDate = dateFormatter.format(date);

  // July issues starting in 2023
  const isDouble = date.getUTCFullYear() >= 2023 && date.getUTCMonth() === 6;
  if (isDouble) {
    displayDate = displayDate.replace("July", "July/August");
  }
  return displayDate;
};

/** Oldest issue year */
export const YEAR_MIN = 1845;
export const YEAR_MAX = new Date().getFullYear() + 1; // Add one to prevent headaches on New Year's Eve

/**
 * Group articles by type
 * @param {{
 *  list: [object] - Array of articles
 * }} list
 * @param {string} property column | category
 */
export const groupByProperty = (list, property = "column") =>
  list.reduce((acc, item) => {
    const articleType = item.article[property] || "";
    acc[articleType] ??= [];
    acc[articleType].push(item);
    return acc;
  }, {});

/**
 * Thumbnail Url restructure
 *
 * This function restructures the image urls to append widths and any extra parameters given.
 *
 * @param {string} urlString - Image url
 * @param {number} width - number value for the width
 * @returns {string} - Updated url
 *
 * @usage
 * thumbnail(image_url, 80, { crop: "1.1", optimize: "smart" }
 */
export const thumbnail = (urlString, width, extra = {}) => {
  const url = new URL(urlString);
  url.searchParams.append("w", width);
  Object.entries(extra).map(([key, value]) => {
    url.searchParams.append(key, value);
  });
  return url.toString();
};

/**
 * Get Parameter or fallback
 * @template [I=any] - Input Type
 * @param {I} param - Parameter
 * @returns {I extends readonly Array ? I[0] : I} Single parameter
 */
export const getParam = (param) => (Array.isArray(param) ? param[0] : param);

/**
 * Convert parameter to array of parameter
 * @template [T=any] - Input Type
 * @param {T|T[]} param - Parameter
 * @returns {T[]} Array of parameter
 */
export const toParams = (param) =>
  Array.isArray(param)
    ? // If param is already an array, return param
      param
    : // If param is null or undefined, return empty array
      // otherwise, return array with param as the only item
      (param == null && []) || [param];

/**
 * Universal checkbox truthiness evaluation
 *
 * This function exists to handle the various ways checkboxes can be interpreted.
 * Consider it a shorthand for coercing form data, query strings, JSON blobs, and external APIs.
 *
 * @param {any} value - Checkbox value
 */
export const isChecked = (value) =>
  value
    ? value === true || // JS does true and false
      value === "on" || // HTML forms do "on" and undefined
      value === "1" || // Query strings may be "1" and "0"
      value >= 1 ||
      value.toLowerCase?.() === "yes" || // Segment does "Yes" and "No"
      value.toLowerCase?.() === "true" // Python does "True" and "False"
    : false;

/**
 * Convert FormData to Object
 *
 * This method is similar to `Object.fromEntries()`, but it handles
 * multiple values for the same input name by mapping them to an array.
 *
 * @usage
 * ```html
 * <form id="form">
 *   <input type="checkbox" name="flavor" value="cherry" checked>
 *   <input type="checkbox" name="flavor" value="vanilla" checked>
 * </form>
 * <script>
 *   const formData = new FormData(document.getElementById('form'));
 *   const object = objectFromFormData(formData);
 *   console.log(object); // { flavor: ['cherry', 'vanilla'] }
 * </script>
 * ```
 * @param {FormData} formData - FormData
 * @returns {Record<string,string|string[]>} Serialized form object
 */
export const objectFromFormData = (formData) => {
  // Turns out the usual Object.fromEntries() doesn't work perfectly with
  // FormData when multiple values are set for the same key.
  // This block of code will map duplicate keys to an array of values.

  // 1. Convert FormData to array of entries
  const formDataObjectWithNameGrouping = Array.from(formData.entries())
    // 2. Reduce each form inputs into an object
    .reduce(
      // This reducer function is executed for every form input entry
      (object, [name, value]) => {
        if (object.hasOwnProperty(name)) {
          // 3. If the input name already exists in the object, map the value to an array
          object[name] = Array.isArray(object[name])
            ? // If the value is already an array, append to the existing array
              [...object[name], value]
            : // Otherwise, create a new array with the existing value and the new value
              [object[name], value];
        } else {
          // 4. Otherwise, set the value as normal
          object[name] = value;
        }

        return object;
      },
      {},
    );

  return formDataObjectWithNameGrouping;
};
